import { PROTOCOL_BYTE_CAPS, assertNever, truncateToUTF8Bytes } from '@somnio/protocol';
import type {
  Condition,
  Energy,
  EntityMove,
  InventoryRowMessage,
  LucidityMessage,
  RaisingMessage,
  SayMessage,
  SomnioMessage,
  TaskMessage,
  TradeMessage,
} from '@somnio/protocol';
import {
  COMBAT,
  heading,
  isTeachingId,
  itemInHand,
  itemWeapon,
  purseCoins,
  swingAllowed,
  swingTarget,
  taskGoal,
  taskSpec,
  windedAfter,
  windedOnJoin,
  withinSpeakingDistance,
} from '@somnio/core';
import type { Heading, ItemId, TaskSpec } from '@somnio/core';
import { bubbleLifetimeMs, canvasWidthMeasurer, wrapSpeech } from '@/scene/speechBubbleText';
import { wheelDeltaToZoomDelta } from '@/scene/cameraRig';
import { parseChatInput } from './chatCommand';
import type { ClientEntity } from './clientWorld';
import { compassPoint } from './compass';
import type { ConnectionController, GameplayMessage } from './connectionController';
import { GameplayPredictor } from './predictor';
import { RemoteInterpolation } from './remoteInterpolation';
import { KeyboardSampler, PlayerZoom, mouseFacingHeading } from './input';
import type { KeyCaptureSink } from './input';

/**
 * The gameplay half of the client: it owns the predictor, the input samplers, and every inbound
 * frame the controller forwards as a `GameplayMessage`.
 *
 * The split is explicit rather than implied. `ConnectionController` handles `hello`, the login and
 * register results, the two session-token frames, and the frames that build the space (`enterSpace`,
 * `sector`, `entity`, `leave`); everything else lands here. Leaving that boundary implicit is how a
 * tag ends up owned by neither half — which the exhaustive switch below and the controller's own
 * `assertNever` guard together make impossible.
 */

const ZERO_ENERGY: Energy = {
  healthCurrent: 0,
  healthMax: 1,
  balanceCurrent: 0,
  balanceMax: 1,
  spiritCurrent: 0,
  spiritMax: 1,
};

const NO_LUCIDITY: LucidityMessage = { ranks: [] };

/** What a task as the wire carries it asks for; `undefined` for a teaching this client does not know or one without a gate. */
export function taskSpecOf(task: TaskMessage): TaskSpec | undefined {
  if (task.teachingId !== undefined && !isTeachingId(task.teachingId)) return undefined;
  return taskSpec({ role: task.role, teachingId: task.teachingId });
}

/** Whether a task as the wire carries it is at its goal. A task this client cannot read has no goal to reach. */
export function taskIsDone(task: TaskMessage): boolean {
  const spec = taskSpecOf(task);
  return spec !== undefined && task.progress >= taskGoal(spec);
}

/** What a left click on the play field does, by what it points at. */
export type ClickAction = 'talk' | 'tend' | 'swing';

/** How long the shop's buttons wait for a trade's answer. A refused trade gets none, so the lock gives way on its own. */
const TRADE_ANSWER_TIMEOUT_MS = 1500;
/** How long they stay locked after the answer, so a double click trades once. */
const TRADE_SETTLE_MS = 500;

export interface GameplaySessionOptions {
  controller: ConnectionController;
  send: (message: SomnioMessage) => void;
  /** Overridable so headless tests drive the tick without a keyboard or a DOM. */
  input?: KeyCaptureSink & { clearHeldKeys(): void };
  /**
   * Text measurer for speech-bubble wrapping. Defaults to the canvas measurer, which the wrap
   * must agree with or lines overflow the balloon; injectable so a headless test needs no canvas.
   */
  measureText?: (line: string) => number;
  /** The clock remote reports are stamped with; the same timeline `runTick` is driven on. */
  now?: () => number;
}

export class GameplaySession {
  readonly predictor: GameplayPredictor;
  readonly zoom = new PlayerZoom();
  readonly input: KeyCaptureSink & { clearHeldKeys(): void };

  energy: Energy = ZERO_ENERGY;
  inventory: InventoryRowMessage[] = [];
  lucidity: LucidityMessage = NO_LUCIDITY;
  /** Whether the player's balance gave out. Kept as the server keeps it: from each `energy` frame, in order. */
  winded = false;
  /** The NPC whose service panel is open. It does not close the input gate: the player walks away from it. */
  servicePanel: ClientEntity | undefined;
  /** The dreamer a Healer chose to tend, as the server was last told. */
  tending: string | undefined;
  /** The raise being given to the fallen player: the Healers holding on, and the whole seconds left. */
  raise: { healerNames: string[]; secondsLeft: number } | undefined;

  /**
   * What has arrived since `enterSpace`. The first frame of each kind in a space states what is,
   * so it sets `winded` from the balance alone and announces nothing; every later one is compared
   * with the one before.
   */
  private seen = { energy: false, inventory: false, lucidity: false };
  private raiseEndsAtMs = 0;
  /** The fallen dreamers whose raise this player was last told they hold on to. */
  private readonly heldRaises = new Set<string>();
  /** Set from sending `wake` until the `enterSpace` that answers it, or until the player stands again because a raise came first. */
  private waking = false;
  /** Whether the button that began a swing is still down, which keeps swinging. */
  private swingHeld = false;
  /** The server's swing rhythm, kept here so a held button asks once a swing rather than once a tick. */
  private swingReadyAtMs = 0;
  private slowedUntilMs = 0;
  /** When the shop's buttons give way, or 0 once the tick has redrawn them free. */
  private tradeLockedUntilMs = 0;

  private readonly controller: ConnectionController;
  private readonly send: (message: SomnioMessage) => void;
  private readonly measureText: (line: string) => number;
  private readonly now: () => number;
  private readonly interpolation = new RemoteInterpolation();
  private latestMouseFacing: Heading | undefined;

  constructor(options: GameplaySessionOptions) {
    this.controller = options.controller;
    this.send = options.send;
    this.input = options.input ?? new KeyboardSampler();
    this.measureText = options.measureText ?? canvasWidthMeasurer();
    this.now = options.now ?? (() => performance.now());
    this.predictor = new GameplayPredictor({
      session: this.controller,
      input: this.input,
      renderSurface: this.controller.renderSurface,
      interpolation: this.interpolation,
      send: this.send,
      mouseFacing: () => this.latestMouseFacing,
      winded: () => this.winded,
      fallen: () => this.fallen,
      slowed: (nowMs) => nowMs < this.slowedUntilMs,
    });

    this.controller.onGameplayMessage = (message) => this.dispatch(message);
    // The controller owns the two predicates the gate reads, so it is also what reports the gate
    // closing. Releasing held keys and a held swing there rather than in each DOM handler is what
    // keeps a stale hold from resuming when focus returns.
    this.controller.onGateClosed = () => {
      this.input.clearHeldKeys();
      this.swingHeld = false;
    };
    this.controller.onSpaceEntered = () => {
      this.resetSpaceState();
      this.latestMouseFacing = undefined;
      if (this.waking) this.controller.appendChat({ kind: 'wokeWeakened' });
      this.waking = false;
    };
    this.controller.onEntityReset = (entityId) => {
      this.interpolation.delete(entityId);
      // A fallen dreamer who leaves takes their raise along without a `raising` frame, so the player holds it no more.
      this.heldRaises.delete(entityId);
      // The player's own entity carries their condition, which is what the fallen notice follows.
      if (entityId === this.controller.selfId) this.onStateChanged?.();
    };
    this.controller.onSelfConditionChanged = (condition) => this.handleOwnCondition(condition);
    this.controller.onTeardown = () => {
      this.resetSpaceState();
      this.input.clearHeldKeys();
      this.energy = ZERO_ENERGY;
      this.inventory = [];
      this.lucidity = NO_LUCIDITY;
      this.winded = false;
      this.waking = false;
    };
  }

  /** Whether the player lies fallen. Their own entity's condition says so, never a health of zero in an `energy` frame. */
  get fallen(): boolean {
    const selfId = this.controller.selfId;
    return selfId !== undefined && this.controller.entities.get(selfId)?.condition === 'fallen';
  }

  /** Everything that belongs to the space being left, a pending door included. */
  private resetSpaceState(): void {
    this.predictor.reset();
    this.predictor.releaseDoor(true);
    this.interpolation.clear();
    this.seen = { energy: false, inventory: false, lucidity: false };
    this.servicePanel = undefined;
    this.raise = undefined;
    this.heldRaises.clear();
    this.swingHeld = false;
    this.swingReadyAtMs = 0;
    this.slowedUntilMs = 0;
    this.tradeLockedUntilMs = 0;
    // The server holds it per space, so a new space starts with no one tended there either.
    this.tending = undefined;
    this.controller.renderSurface.showSelection(undefined);
  }

  /** The per-frame body. Injected timestamp, so a test drives it directly. */
  runTick(timestampMs: number): void {
    this.predictor.runTick(timestampMs);
    this.closeServicePanelBeyondSpeakingDistance();
    this.countDownRaise(timestampMs);
    if (this.tending !== undefined && !this.controller.entities.has(this.tending)) this.tend(undefined);
    if (this.swingHeld) this.swing(timestampMs);
    if (this.tradeLockedUntilMs !== 0 && !this.tradeLocked()) {
      this.tradeLockedUntilMs = 0;
      this.onStateChanged?.();
    }
  }

  private get self(): ClientEntity | undefined {
    const selfId = this.controller.selfId;
    return selfId === undefined ? undefined : this.controller.entities.get(selfId);
  }

  /** Whether a click acts on the world: attached, no overlay up, and standing. */
  private get acting(): boolean {
    return this.controller.connectionState === 'attached' && this.controller.presentedOverlay === undefined && !this.fallen;
  }

  /**
   * What a left click at an entity, or at none, would do. An NPC is asked to go on from within
   * speaking distance, and from further away the click does nothing. A Healer's click at another dreamer
   * tends them. Everything else is a swing toward the cursor.
   */
  clickAction(entityId: string | undefined): ClickAction | undefined {
    const self = this.self;
    if (self === undefined || !this.acting) return undefined;
    const entity = entityId === undefined ? undefined : this.controller.entities.get(entityId);
    if (entity?.kind === 'npc') return withinSpeakingDistance(self.position, entity.position) ? 'talk' : undefined;
    if (entity?.kind === 'peer' && this.lucidity.role === 'healer') return 'tend';
    return 'swing';
  }

  /**
   * A left click on the play field at `entityId`, or at nothing. Clicking the dreamer tended lets
   * go of them, and so does a swing. A new press ends a swing still held from the one before it.
   */
  pressAt(entityId: string | undefined, timestampMs: number): void {
    this.swingHeld = false;
    switch (this.clickAction(entityId)) {
      case 'talk':
        this.send({ tag: 'talk', payload: { npcId: entityId! } });
        if (this.controller.entities.get(entityId!)?.service !== undefined) this.openServicePanel(entityId!);
        return;
      case 'tend':
        this.tend(entityId === this.tending ? undefined : entityId);
        return;
      case 'swing':
        this.tend(undefined);
        this.swingHeld = true;
        this.swing(timestampMs);
        return;
      case undefined:
        return;
    }
  }

  /** The button that began a swing came up, or the pointer was lost. */
  release(): void {
    this.swingHeld = false;
  }

  /** Tends `entityId` from now on, or no one, and tells the server when that is a change. */
  tend(entityId: string | undefined): void {
    if (entityId === this.tending) return;
    this.tending = entityId;
    this.send({ tag: 'tend', payload: entityId === undefined ? {} : { targetId: entityId } });
    this.controller.renderSurface.showSelection(entityId);
    this.onStateChanged?.();
  }

  /**
   * Asks for one swing when the server would make one: the swing rhythm has come round, what is in
   * hand swings, no task forbids it, and the balance covers it. The position is reported first, so
   * the server judges the reach from where the player stands. The swing slows the player at once,
   * without waiting for the `blow` that answers it.
   */
  private swing(nowMs: number): void {
    const self = this.self;
    if (self === undefined || !this.acting || nowMs < this.swingReadyAtMs) return;
    const task = this.lucidity.task === undefined ? undefined : taskSpecOf(this.lucidity.task);
    const swing = swingAllowed(task, itemInHand(this.inventory), this.lucidity.ranks, this.energy.balanceCurrent);
    if (swing === undefined) return;
    this.swingReadyAtMs = nowMs + swing.seconds * 1000;
    this.slowedUntilMs = nowMs + COMBAT.swingSlow.seconds * 1000;
    const nightmares = [...this.controller.entities.values()]
      .filter((entity) => entity.kind === 'monster' && entity.condition !== 'fallen')
      .map((entity) => ({ id: entity.id, x: entity.position.x, z: entity.position.z, radius: entity.radius }));
    const met = swingTarget(self.position, this.latestMouseFacing ?? self.facing, nightmares);
    this.predictor.reportNow();
    this.send({ tag: 'swing', payload: met === undefined ? {} : { targetId: met.id } });
  }

  private openServicePanel(npcId: string): void {
    if (this.servicePanel?.id === npcId) return;
    this.servicePanel = this.controller.entities.get(npcId);
    this.onStateChanged?.();
  }

  closeServicePanel(): void {
    if (this.servicePanel === undefined) return;
    this.servicePanel = undefined;
    this.onStateChanged?.();
  }

  /** The panel belongs to standing with the NPC: walking out of speaking distance closes it, as the server would drop a request from there. */
  private closeServicePanelBeyondSpeakingDistance(): void {
    const self = this.self;
    const npc = this.servicePanel;
    if (npc === undefined) return;
    if (self === undefined || !withinSpeakingDistance(self.position, npc.position)) this.closeServicePanel();
  }

  /**
   * Counts the raise down between snapshots, and never up: a frame's timestamp can lie a little
   * before the moment the last snapshot arrived, which would otherwise read a whole second more.
   */
  private countDownRaise(timestampMs: number): void {
    if (this.raise === undefined) return;
    const secondsLeft = Math.min(this.raise.secondsLeft, Math.max(0, Math.ceil((this.raiseEndsAtMs - timestampMs) / 1000)));
    if (secondsLeft === this.raise.secondsLeft) return;
    this.raise = { ...this.raise, secondsLeft };
    this.onStateChanged?.();
  }

  private handleOwnCondition(condition: Condition): void {
    if (condition === 'fallen') {
      this.servicePanel = undefined;
      // Falling lets go of the dreamer tended, as the server does: nothing sent while fallen could.
      this.tending = undefined;
      this.controller.renderSurface.showSelection(undefined);
      this.controller.appendChat({ kind: 'fell' });
    } else {
      // Standing again without an `enterSpace` means a raise came first, and the `wake` was dropped.
      this.waking = false;
    }
    this.onStateChanged?.();
  }

  /**
   * Clears input state that a `keyup` delivered while the page was hidden would have left
   * populated, and drops the tick clock so the first tick back measures zero elapsed rather than
   * the whole hidden interval. The clamp already bounds that interval, but resetting is what makes
   * the resumed tick behave identically to the first tick of a session. A pending door stays
   * pending: the server is still answering it.
   */
  handleVisibilityLoss(): void {
    this.input.clearHeldKeys();
    this.swingHeld = false;
    this.predictor.reset();
  }

  updateMouseFacing(pointer: { x: number; y: number }, center: { x: number; y: number }): void {
    this.latestMouseFacing = mouseFacingHeading(pointer, center);
  }

  /**
   * Applies one wheel event.
   *
   * Both the sign and the *scale* have to be converted. Negated because the DOM's positive `deltaY`
   * scrolls down while the zoom's convention is positive for up; rescaled because a DOM pixel delta
   * is ~30x the notch-scale delta `PLAYER_ZOOM`'s gain is tuned against. `deltaMode`
   * matters too — Firefox reports lines where Chrome reports pixels.
   */
  applyScrollZoom(deltaY: number, deltaMode = 0): boolean {
    return this.zoom.applyScroll(-wheelDeltaToZoomDelta(deltaY, deltaMode));
  }

  /**
   * Says a chat line plainly, or as the whisper or yell its command names. The text is capped in
   * **UTF-8 bytes**, not code units: the server rejects on byte length, so a string of 200 emoji
   * passes a `.length <= 256` check and is then refused. An unknown command sends nothing and is answered in chat.
   */
  submitChat(rawText: string): void {
    const self = this.self;
    if (this.controller.connectionState !== 'attached' || self === undefined) return;
    const input = parseChatInput(rawText);
    if (input === undefined) return;
    if ('unknownCommand' in input) {
      this.controller.appendChat({ kind: 'unknownCommand', command: input.unknownCommand });
      return;
    }
    const text = truncateToUTF8Bytes(input.text, PROTOCOL_BYTE_CAPS.say);
    this.send({ tag: 'clientSay', payload: { text, kind: input.kind } });
    this.controller.appendChat({
      kind: 'spokenByOwn',
      senderName: this.controller.selfDisplayName,
      message: text,
      speech: input.kind,
    });
    const lines = wrapSpeech(text, this.measureText);
    this.controller.renderSurface.showSpeechBubble({
      entityId: self.id,
      source: self.position,
      lines,
      lifetimeMs: bubbleLifetimeMs(lines.length),
      kind: input.kind,
      clarity: 1,
    });
  }

  /**
   * Double-click activation. A weapon toggles equip in its fixed hand — the player never picks
   * one — and the purse reports its balance to the chat log rather than equipping. The Moonstone
   * is taken in that same hand, which puts the weapon away, and used once it is there.
   */
  activateInventoryRow(row: InventoryRowMessage): void {
    if (this.controller.connectionState !== 'attached') return;
    if (itemWeapon(row.itemId) !== undefined) {
      // Re-toggling leaves the hand out to unequip; the server clears whatever else held it.
      this.send({ tag: 'equipToggle', payload: { slot: row.slot, ...(row.equippedHand === undefined ? { hand: 'right' } : {}) } });
      return;
    }
    switch (row.itemId) {
      case 'purse' satisfies ItemId:
        this.controller.appendChat({ kind: 'purseBalance', coins: row.quantity });
        return;
      case 'moonstone' satisfies ItemId:
        if (row.equippedHand === 'right') this.send({ tag: 'useItem', payload: { slot: row.slot } });
        else this.send({ tag: 'equipToggle', payload: { slot: row.slot, hand: 'right' } });
        return;
    }
  }

  /** Asks the master whose panel is open for a task: their trial, or the one that gates `teachingId`. */
  askTask(teachingId?: string): void {
    if (this.servicePanel === undefined) return;
    this.send({ tag: 'askTask', payload: { npcId: this.servicePanel.id, ...(teachingId === undefined ? {} : { teachingId }) } });
  }

  completeTask(): void {
    if (this.servicePanel !== undefined) this.send({ tag: 'completeTask', payload: { npcId: this.servicePanel.id } });
  }

  abandonTask(): void {
    this.send({ tag: 'abandonTask', payload: {} });
  }

  study(teachingId: string): void {
    if (this.servicePanel !== undefined) this.send({ tag: 'study', payload: { npcId: this.servicePanel.id, teachingId } });
  }

  /** Asks the shopkeeper whose panel is open to sell one of a ware. */
  buy(itemId: string): void {
    this.trade('buy', itemId);
  }

  /** Asks the shopkeeper whose panel is open to buy back a ware. */
  sell(itemId: string): void {
    this.trade('sell', itemId);
  }

  /** Whether the shop's buttons are locked, from a trade until a moment after its answer. */
  tradeLocked(): boolean {
    return this.now() < this.tradeLockedUntilMs;
  }

  private trade(tag: 'buy' | 'sell', itemId: string): void {
    const npc = this.servicePanel;
    if (npc?.service !== 'shopkeeper' || this.tradeLocked()) return;
    this.send({ tag, payload: { npcId: npc.id, itemId } });
    this.tradeLockedUntilMs = this.now() + TRADE_ANSWER_TIMEOUT_MS;
    this.onStateChanged?.();
  }

  /** Gives up: the server answers a fallen dreamer with the `enterSpace` of the wake-point. */
  wake(): void {
    if (!this.fallen) return;
    this.waking = true;
    this.send({ tag: 'wake', payload: {} });
  }

  private dispatch(message: GameplayMessage): void {
    switch (message.tag) {
      case 'moves':
        for (const move of message.payload.moves) this.handleMove(move);
        return;
      case 'correction':
        this.predictor.correct(message.payload);
        return;
      case 'doorRefused':
        this.predictor.releaseDoor(false);
        return;
      case 'serverSay':
        this.handleServerSay(message.payload);
        return;
      case 'energy':
        this.winded = this.seen.energy ? windedAfter(this.winded, message.payload.balanceCurrent) : windedOnJoin(message.payload.balanceCurrent);
        this.seen.energy = true;
        this.energy = message.payload;
        this.onStateChanged?.();
        return;
      case 'inventory': {
        const trade = message.payload.trade;
        if (trade !== undefined) {
          // A trade is its own news: the coins it moved are not announced again.
          this.announceTrade(trade);
          this.tradeLockedUntilMs = this.now() + TRADE_SETTLE_MS;
        } else {
          const gained = purseCoins(message.payload.rows) - purseCoins(this.inventory);
          if (this.seen.inventory && gained > 0) this.controller.appendChat({ kind: 'coinsGained', coins: gained });
        }
        this.seen.inventory = true;
        this.inventory = message.payload.rows;
        this.onStateChanged?.();
        return;
      }
      case 'lucidity':
        if (this.seen.lucidity) this.announceLucidity(this.lucidity, message.payload);
        this.seen.lucidity = true;
        this.lucidity = message.payload;
        this.onStateChanged?.();
        return;
      case 'blow':
        this.controller.renderSurface.showBlow(message.payload.attackerId, message.payload.targetId, message.payload.hit);
        return;
      case 'raising':
        this.handleRaising(message.payload);
        return;
      case 'mend':
        this.controller.renderSurface.showMend(message.payload.targetId);
        return;
      case 'adminSay':
        this.controller.appendChat({ kind: 'adminBroadcast', message: message.payload.text });
        return;
      default:
        // Exhaustive over `GameplayMessage`, so a tag added to that union without a case here is a
        // compile error rather than a silently dropped frame.
        assertNever(message, 'gameplay dispatch');
    }
  }

  onStateChanged: (() => void) | undefined;

  private announceTrade({ kind, itemId, coins }: TradeMessage): void {
    switch (kind) {
      case 'bought':
        this.controller.appendChat({ kind, itemId, coins });
        break;
      case 'sold': {
        const emptiedHand = itemInHand(this.inventory) === itemId;
        this.controller.appendChat({ kind, itemId, coins, emptiedHand });
        break;
      }
    }
  }

  /** The chat lines for what changed from one `lucidity` frame to the next. */
  private announceLucidity(before: LucidityMessage, after: LucidityMessage): void {
    if (before.role === undefined && after.role !== undefined) this.controller.appendChat({ kind: 'becameRole', role: after.role });
    for (const held of after.ranks) {
      const had = before.ranks.find((other) => other.teachingId === held.teachingId)?.rank ?? 0;
      if (held.rank > had && isTeachingId(held.teachingId)) this.controller.appendChat({ kind: 'rankGained', teachingId: held.teachingId, rank: held.rank });
    }
    if (before.task !== undefined && after.task !== undefined && !taskIsDone(before.task) && taskIsDone(after.task)) {
      this.controller.appendChat({ kind: 'taskDone' });
    }
  }

  /**
   * The scene shows every raise as it stands. A Healer is told when a raise they hold on to is
   * done, and when they lose hold of it while still tending the dreamer with the Moonstone in hand.
   * The dreamer being raised is shown who draws them back and for how long, and told when it is
   * done or breaks.
   */
  private handleRaising(raising: RaisingMessage): void {
    const { targetId, healerIds, state } = raising;
    this.controller.renderSurface.showRaising(targetId, state === 'holding' ? { progress: raising.progress, seconds: raising.seconds } : undefined);
    const selfId = this.controller.selfId;
    const named = selfId !== undefined && healerIds.includes(selfId);
    if (named || this.heldRaises.has(targetId)) {
      const targetName = this.controller.entities.get(targetId)?.name ?? '';
      const holding = state === 'holding' && named;
      if (state === 'done') {
        if (named) this.controller.appendChat({ kind: 'raisedOther', targetName });
      } else if (this.heldRaises.has(targetId) && !holding && this.tending === targetId && itemInHand(this.inventory) === ('moonstone' satisfies ItemId)) {
        // Still tending with the stone in hand, so they did not let go. Letting go clears `tending`, and
        // so does a fall, whose `condition` comes a step before the drop. Putting the stone away keeps
        // `tending`, but its `inventory` frame also comes before the drop.
        this.controller.appendChat({ kind: 'raiseBrokenOther', targetName });
      }
      if (holding) this.heldRaises.add(targetId);
      else this.heldRaises.delete(targetId);
      return;
    }
    if (targetId !== selfId) return;
    const names = healerIds.map((id) => this.controller.entities.get(id)?.name ?? '');
    if (state === 'holding') {
      this.raiseEndsAtMs = this.now() + raising.seconds * 1000;
      this.raise = { healerNames: names, secondsLeft: Math.ceil(raising.seconds) };
    } else {
      // The names kept from the last snapshot, which held the same Healers: one who left is out of view by now.
      const healerNames = this.raise?.healerNames ?? names;
      this.raise = undefined;
      this.controller.appendChat({ kind: state === 'done' ? 'raised' : 'raiseBroken', healerNames });
    }
    this.onStateChanged?.();
  }

  /**
   * One remote entity's report. The entity keeps the position it is drawn at; the report becomes
   * the target it glides to, which the predictor samples every tick. The local player never
   * appears here: its position is predicted, and the server's only word on it is `correction`.
   */
  private handleMove(move: EntityMove): void {
    const entity = this.controller.entities.get(move.id);
    if (entity === undefined || entity.id === this.controller.selfId) return;
    this.controller.entities.set(move.id, { ...entity, facing: heading(move.facing), gait: move.gait });
    this.controller.renderSurface.updateGait(move.id, move.gait);
    this.interpolation.retarget(move.id, entity.position, { x: move.x, z: move.z }, this.now());
  }

  /**
   * NPC dialog arrives here, not on a dedicated tag: there is no NPC-dialog verb. A voice comes from
   * the speaker's body when the client has one, and otherwise from where the server says (a speaker
   * out of view, or behind a door). The bubble follows the body once it arrives. When the scene pins
   * the bubble at the screen's edge, the chat line names the direction of the voice. The voice and
   * the direction are both taken from where the bodies stand as the line arrives. A hidden tab runs
   * no tick, so the positions last drawn there are stale.
   */
  private handleServerSay(say: SayMessage): void {
    this.predictor.drawRemoteEntities(this.now());
    const body = this.controller.entities.get(say.entityId);
    const voice = body?.position ?? { x: say.x, z: say.z };
    const lines = wrapSpeech(say.text, this.measureText);
    const pinned = this.controller.renderSurface.showSpeechBubble({
      entityId: say.entityId,
      source: voice,
      lines,
      lifetimeMs: bubbleLifetimeMs(lines.length),
      kind: say.kind,
      clarity: say.clarity,
    });
    const self = this.self;
    const direction = pinned && self !== undefined ? compassPoint(self.position, voice) : undefined;
    this.controller.appendChat({
      kind: body?.kind === 'npc' || body?.kind === 'monster' ? 'spokenByNPC' : 'spokenByPeer',
      senderName: say.name,
      message: say.text,
      speech: say.kind,
      ...(direction === undefined ? {} : { direction }),
    });
  }
}
